import React, { Suspense, lazy, useState, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";


// Lazy-load Pages
const Index = lazy(() => import("./pages/Index"));
const CustomizePage = lazy(() => import("./pages/CustomizePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const OrderTrackingPage = lazy(() => import("./pages/OrderTrackingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ComponentsPage = lazy(() => import("./pages/ComponentsPage"));
const GamingPCsPage = lazy(() => import("./pages/GamingPCsPage"));
const PrebuiltDetailsPage = lazy(() => import("./pages/PrebuiltDetailsPage"));
const WorkstationsPage = lazy(() => import("./pages/WorkstationsPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const OrderCancelledPage = lazy(() => import("./pages/OrderCancelledPage"));
const AffiliateApplicationPage = lazy(() => import("./pages/AffiliateApplicationPage"));
const AffiliateDashboardPage = lazy(() => import("./pages/AffiliateDashboardPage"));
const AffiliateStatusPage = lazy(() => import("./pages/AffiliateStatusPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const CustomerPortalPage = lazy(() => import("./pages/CustomerPortalPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const MarketplaceSellerPage = lazy(() => import("./pages/MarketplaceSellerPage"));
const MarketplaceProductPage = lazy(() => import("./pages/MarketplaceProductPage"));
const MarketplaceSellerEditPage = lazy(() => import("./pages/MarketplaceSellerEditPage"));
const MarketplaceSellerSalesPage = lazy(() => import("./pages/MarketplaceSellerSalesPage"));
const MarketplaceSellerProfilePage = lazy(() => import("./pages/MarketplaceSellerProfilePage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const ArticlesListPage = lazy(() => import("./pages/ArticlesListPage"));
const CustomCheckoutPage = lazy(() => import('./pages/CustomCheckoutPage'));
const ARViewerPage = lazy(() => import('./pages/ARViewerPage'));
const AccessoriesPage = lazy(() => import('./pages/AccessoriesPage'));
const AccessoryDetailsPage = lazy(() => import('./pages/AccessoryDetailsPage'));
const PCBuildingGuidePage = lazy(() => import("./pages/PCBuildingGuidePage"));
const BuildTemplatesPage = lazy(() => import('./pages/BuildTemplatesPage'));
const BuildTemplateDetailsPage = lazy(() => import('./pages/BuildTemplateDetailsPage'));

// Providers and Components
import { AuthProvider } from "./hooks/useAuth";
import { CartProvider } from "./hooks/use-cart";
import { PCConfigProvider } from "./hooks/use-pc-configuration";
import { MobileProvider } from "./hooks/use-mobile";
import { CheckoutProvider } from "./hooks/use-checkout";
import { CustomizerStateProvider } from "./hooks/use-customizer-state";
import { MarketplaceProvider } from "./hooks/use-marketplace";
import CrispChat from "./components/Chat/CrispChat";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

// Create a client
const queryClient = new QueryClient();

const FullPageLoader = () => (
  <div className="w-full h-screen flex items-center justify-center bg-gaming-dark">
    <Loader2 className="h-12 w-12 animate-spin text-gaming-blue" />
  </div>
);

function App() {
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    // Delay loading CrispChat to prioritize interactive content
    const timer = setTimeout(() => {
      setShowChat(true);
    }, 5000); // 5-second delay

    return () => clearTimeout(timer);
  }, []);

  return (
      <QueryClientProvider client={queryClient}>
        <MobileProvider>
          <AuthProvider>
            <CartProvider>
              <PCConfigProvider>
                <CheckoutProvider>
                  <CustomizerStateProvider>
                    <MarketplaceProvider>
                      <Suspense fallback={<FullPageLoader />}>
                        <Routes>
                          <Route path="/" element={<Index />} />

                          <Route path="/customize" element={<CustomizePage />} />
                          <Route path="/about" element={<AboutPage />} />
                          <Route path="/support" element={<SupportPage />} />
                          <Route path="/admin" element={<AdminPage />} />
                          <Route path="/checkout" element={<CheckoutPage />} />
                          <Route path="/order-tracking" element={<OrderTrackingPage />} />
                          <Route path="/auth" element={<AuthPage />} />
                          <Route path="/components" element={<ComponentsPage />} />
                          <Route path="/components/:category" element={<ComponentsPage />} />
                          <Route path="/gaming-pcs" element={<GamingPCsPage />} />
                          <Route path="/gaming-pcs/:slug" element={<PrebuiltDetailsPage />} />
                          <Route path="/workstations" element={<WorkstationsPage />} />
                          <Route path="/terms" element={<TermsPage />} />
                          <Route path="/privacy" element={<PrivacyPage />} />
                          <Route path="/order-success" element={<OrderSuccessPage />} />
                          <Route path="/cancelled" element={<OrderCancelledPage />} />
                          <Route path="/checkout/cancelled" element={<OrderCancelledPage />} />
                          <Route path="/affiliate/apply" element={<AffiliateApplicationPage />} />
                          <Route path="/affiliate/dashboard" element={<AffiliateDashboardPage />} />
                          <Route path="/affiliate/status" element={<AffiliateStatusPage />} />
                          <Route path="/sitemap" element={<SitemapPage />} />
                          <Route path="/account" element={<CustomerPortalPage />} />
                          <Route path="/marketplace" element={<MarketplacePage />} />
                          <Route path="/marketplace/sell" element={<MarketplaceSellerPage />} />
                          <Route path="/marketplace/product/:id" element={<MarketplaceProductPage />} />
                          <Route path="/marketplace/seller/edit" element={<MarketplaceSellerEditPage />} />
                          <Route path="/marketplace/seller/sales" element={<MarketplaceSellerSalesPage />} />
                          <Route path="/marketplace/seller/:slug" element={<MarketplaceSellerProfilePage />} />
                          <Route path="/article/:slug" element={<ArticlePage />} />
                          <Route path="/articles" element={<ArticlesListPage />} />
                          <Route path="/custom-checkout/:linkId" element={<CustomCheckoutPage />} />
                          <Route path="/ar-viewer" element={<ARViewerPage />} />
                          <Route path="/accessories" element={<AccessoriesPage />} />
                          <Route path="/accessories/:id" element={<AccessoryDetailsPage />} />
                          <Route path="/pc-building-guide" element={<PCBuildingGuidePage />} />
                          <Route path="/build-templates" element={<BuildTemplatesPage />} />
              <Route path="/build-templates/:id" element={<BuildTemplateDetailsPage />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                      {showChat && <CrispChat />}
                    </MarketplaceProvider>
                  </CustomizerStateProvider>
                </CheckoutProvider>
              </PCConfigProvider>
            </CartProvider>
          </AuthProvider>
        </MobileProvider>
        <Toaster />
      </QueryClientProvider>
  );
}

export default App;
